'use client';

import AuthenticatedContainer from '@/components/AuthenticatedContainer';
import Layout from '@/components/Layout';
import createEmotionCache from '@/createEmotionCache';
import { SWRProvider } from '@/services/hooks';
import '@/services/index';
import { APPLICATION_NAME } from '@/utils/constants';
import { META_DESCRIPTION } from '@/utils/messages';
import { EmotionCache } from '@emotion/react';
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { Passwordless, PasswordlessContextProvider } from '@verifime/cognito-passwordless-auth';
import {
  ConfirmProvider,
  ErrorNotification,
  FavIcons,
  GlobalStyleOverride,
  SuccessNotification,
} from '@verifime/components';
import '@verifime/design-tokens/dist/tokens.css';
import { ToggleTheme } from '@verifime/styles';
import '@verifime/styles/styles/globals.css';
import { isClient } from '@verifime/utils';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { SnackbarProvider } from 'notistack';
import { NAV_ITEM_HOME, NAV_ITEM_PERMISSIONS, NAV_ITEM_SETTINGS, NAV_ITEM_TASKS } from '../path';

if (isClient) {
  Passwordless.configure({
    cognitoIdpEndpoint: process.env.NEXT_PUBLIC_COGNITO_IDP_ENDPOINT,
    clientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
    userPoolId: process.env.NEXT_PUBLIC_COGNITO_USERPOOL_ID,
    storage: window.localStorage,
    fido2: {
      baseUrl: process.env.NEXT_PUBLIC_FIDO2_BASE_URL,
      authenticatorSelection: {
        userVerification: 'required',
      },
      rp: {
        id: process.env.NEXT_PUBLIC_FIDO2_RELYING_PARTY_ID, // default is not set and fallback to hostname e.g. localhost
      },
    },
    totp: {
      baseUrl: process.env.NEXT_PUBLIC_TOTP_BASE_URL,
      issuer: process.env.NEXT_PUBLIC_TOTP_ISSUER ?? 'VerifiMeDev',
    },
    debug: console.debug,
  });
}

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

const PATH_WITH_LAYOUT: string[] = [
  NAV_ITEM_HOME.path,
  NAV_ITEM_PERMISSIONS.path,
  NAV_ITEM_TASKS.path,
  NAV_ITEM_SETTINGS.path,
  `${NAV_ITEM_HOME.path}/[id]`, // identity details page
];

type TAppProps = {
  emotionCache: EmotionCache;
} & AppProps;

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

const App = ({ Component, emotionCache = clientSideEmotionCache, pageProps }: TAppProps) => {
  const router = useRouter();

  return (
    <SWRProvider>
      <QueryClientProvider client={queryClient}>
        <AppCacheProvider emotionCache={emotionCache}>
          <Head>
            <title>{APPLICATION_NAME}</title>
            <meta name="description" content={META_DESCRIPTION} />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <FavIcons />
          </Head>

          <ToggleTheme>
            <GlobalStyleOverride />
            <SnackbarProvider
              Components={{
                successNotification: SuccessNotification,
                errorNotification: ErrorNotification,
              }}
            >
              <ConfirmProvider>
                <PasswordlessContextProvider enableLocalUserCache={true}>
                  <AuthenticatedContainer>
                    {PATH_WITH_LAYOUT.includes(router.pathname) ? (
                      <Layout>
                        <Component {...pageProps} />
                      </Layout>
                    ) : (
                      <Component {...pageProps} />
                    )}
                  </AuthenticatedContainer>
                </PasswordlessContextProvider>
              </ConfirmProvider>
            </SnackbarProvider>
          </ToggleTheme>
        </AppCacheProvider>
      </QueryClientProvider>
    </SWRProvider>
  );
};

export default App;
